// The revenue schedule editor's draft: the rows as typed, what changed, and the
// checks the server makes again (revenueScheduleEdits.mjs) so Save can say
// what is wrong before it is pressed.

export interface EditorLine {
  id: string;
  name: string;
  billing: string;
  /** What the quote sells the line for over its schedule; null until it has a start. */
  quote_total: number | null;
  schedule_total: number;
  /** Set when the line's schedule was edited by hand; regenerating the quote keeps it. */
  edited_at: string | null;
  edited_by: string | null;
}

export interface EditorRow {
  id: string | null;
  quote_line_id: string;
  period_start: string;
  period_end: string;
  amount: number;
  hours: number | null;
}

export interface ScheduleEditorPayload {
  quote: { id: string; quote_number: string | null; name: string | null } | null;
  lines: EditorLine[];
  rows: EditorRow[];
  version: string | null;
}

/** A row in the grid: a stable key, and the values as typed. */
export interface DraftRow {
  key: string;
  quote_line_id: string;
  period_start: string;
  period_end: string;
  amount: string;
  hours: string;
}

export const toDraft = (rows: EditorRow[]): DraftRow[] =>
  rows.map((r, i) => ({
    key: r.id ?? `row-${i}`,
    quote_line_id: r.quote_line_id,
    period_start: r.period_start,
    period_end: r.period_end,
    amount: String(r.amount),
    hours: r.hours === null ? '' : String(r.hours),
  }));

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const time = (date: string): number | null => {
  if (!DATE.test(date)) return null;
  const t = Date.parse(`${date}T00:00:00Z`);
  return Number.isNaN(t) || new Date(t).toISOString().slice(0, 10) !== date ? null : t;
};
const iso = (t: number) => new Date(t).toISOString().slice(0, 10);
const DAY = 86_400_000;
/** `t` plus a month, keeping the day where the month has it (Jan 31 → Feb 28). */
const addMonth = (t: number) => {
  const d = new Date(t);
  const lastDay = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 2, 1) - DAY).getUTCDate();
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, Math.min(d.getUTCDate(), lastDay));
};
const number = (text: string): number | null => {
  const trimmed = text.replace(/[$,\s]/g, '');
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
};
const cents = (n: number) => Math.round(n * 100) / 100;

/** A new row for a line: the month after its last row, for nothing. */
export function newRow(draft: DraftRow[], lineId: string, key: string): DraftRow {
  const last = draft
    .filter((r) => r.quote_line_id === lineId)
    .map((r) => time(r.period_start))
    .filter((t): t is number => t !== null)
    .sort((a, b) => a - b)
    .at(-1);
  const now = new Date();
  const start = last === undefined ? Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1) : addMonth(last);
  return { key, quote_line_id: lineId, period_start: iso(start), period_end: iso(addMonth(start) - DAY), amount: '0', hours: '' };
}

/** The draft's rows as the server takes them; null where a value is not a number. */
export function toWire(draft: DraftRow[]): (Omit<EditorRow, 'amount'> & { amount: number | null })[] {
  return draft.map((r) => ({
    id: r.key.startsWith('new-') ? null : r.key,
    quote_line_id: r.quote_line_id,
    period_start: r.period_start,
    period_end: r.period_end,
    amount: number(r.amount),
    hours: number(r.hours),
  }));
}

type Keyed = { quote_line_id: string; period_start: string; period_end: string; amount: number | null; hours: number | null };
const rowKey = (r: Keyed) =>
  `${r.period_start}|${r.period_end}|${r.amount === null ? '' : cents(r.amount)}|${r.hours ?? ''}`;

/** The lines whose rows differ from what was loaded, in any order. */
export function changedLines(lines: EditorLine[], loaded: EditorRow[], draft: DraftRow[]): Set<string> {
  const wire = toWire(draft);
  const of = (rows: Keyed[], id: string) => rows.filter((r) => r.quote_line_id === id).map(rowKey).sort().join(';');
  return new Set(lines.map((l) => l.id).filter((id) => of(loaded, id) !== of(wire, id)));
}

/** Each line's total in the draft. */
export function draftTotals(draft: DraftRow[]): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const r of draft) totals[r.quote_line_id] = cents((totals[r.quote_line_id] ?? 0) + (number(r.amount) ?? 0));
  return totals;
}

/** A line's total less its quote total, to the cent; 0 when they match. */
export const gapOf = (total: number, quoteTotal: number): number => {
  const gap = cents(total - quoteTotal);
  return Math.abs(gap) < 0.005 ? 0 : gap;
};

/** What stops a save, by row number and line name, as the server would say it. */
export function problems(lines: EditorLine[], loaded: EditorRow[], draft: DraftRow[], money: (n: number) => string): string[] {
  const found: string[] = [];
  draft.forEach((r, i) => {
    const n = `Row ${i + 1}`;
    const start = time(r.period_start);
    const end = time(r.period_end);
    if (start === null) found.push(`${n}: the start is not a date.`);
    else if (end === null) found.push(`${n}: the end is not a date.`);
    else if (end < start) found.push(`${n} ends before it starts.`);
    else if (end > addMonth(start) - DAY) found.push(`${n} is longer than a month: split it into a row per month.`);
    if (number(r.amount) === null) found.push(`${n}: the amount is not a number.`);
    const hours = number(r.hours);
    if (r.hours.trim() !== '' && (hours === null || hours < 0)) found.push(`${n}: hours must be a number from 0.`);
  });
  const changed = changedLines(lines, loaded, draft);
  const totals = draftTotals(draft);
  for (const line of lines) {
    if (!changed.has(line.id)) continue;
    if (line.quote_total === null) {
      found.push(`${line.name}: give the quote a kickoff on the Terms tab, or the line a start date, before editing its schedule.`);
      continue;
    }
    const gap = gapOf(totals[line.id] ?? 0, line.quote_total);
    if (gap !== 0) {
      found.push(`${line.name} adds up to ${money(totals[line.id] ?? 0)}, ${money(Math.abs(gap))} ${gap < 0 ? 'short of' : 'over'} the quote's ${money(line.quote_total)}.`);
    }
  }
  return found;
}

/** The grid in date order, then line order, as the server returns it. */
export function sortDraft(draft: DraftRow[], lines: EditorLine[]): DraftRow[] {
  const order = new Map(lines.map((l, i) => [l.id, i]));
  return [...draft].sort((a, b) => a.period_start.localeCompare(b.period_start) || (order.get(a.quote_line_id) ?? 0) - (order.get(b.quote_line_id) ?? 0));
}
